import { t, useLanguage } from "@/i18n/LanguageContext";import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, ChevronDown } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import DynIcon from "@/components/DynIcon";
import Marquee from "@/components/Marquee";
import { Reveal, Eyebrow, DecorativeStripe } from "@/components/Reveal";
import { Macroareas, SportCommunity, CareerCallout } from "@/components/CompanySections";
import { HeroVideo } from "@/components/HeroVideo";
import { DesignPhilosophySection } from "@/components/DesignPhilosophySection";
import { BrochureDownload } from "@/components/BrochureDownload";
import { CertificationBackdrop } from "@/components/CertificationBackdrop";
import { HomeWorkGallery } from "@/components/HomeWorkGallery";

function Counter({ value }) {useLanguage();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  const num = parseInt(value, 10);
  const suffix = value.replace(/[0-9]/g, "");

  useEffect(() => {
    if (!inView || isNaN(num)) return;
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / 1500, 1);
      setN(Math.round(num * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, num]);

  return (
    <span ref={ref} data-testid={`stat-value-${value}`}>
      {t(isNaN(num) ? value : `${n}${suffix}`)}
    </span>);

}

const BENTO_SPANS = ["md:col-span-3", "md:col-span-3", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-3", "md:col-span-3", "md:col-span-2", "md:col-span-2", "md:col-span-2"];

export default function Home() {useLanguage();
  const { content } = useSite();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const lines = [content.hero.line1, content.hero.line2, content.hero.line3];

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative -mt-16 flex min-h-[100svh] items-center overflow-hidden bg-brand-navydeep pt-16" data-testid="hero-section">
        <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
          {t(content.hero.video ?
          <HeroVideo src={content.hero.video} poster={content.hero.image} /> :

          <img src={content.hero.image} alt={t("Cantiere GFC")} className="h-full w-full object-cover opacity-75" />)
          }
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navydeep/95 via-brand-navydeep/25 to-transparent" />
          <div className="absolute inset-y-0 left-0 w-full bg-gradient-to-r from-brand-navydeep/60 via-transparent to-transparent lg:w-2/3" />
        </motion.div>

        <div className="relative mx-auto w-full max-w-7xl px-4 py-28 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-brand-limebright/60 bg-black/80 px-4 py-1.5"
            data-testid="hero-badge">
            
            <span className="h-2 w-2 animate-pulse rounded-full bg-brand-limebright" />
            <span data-testid="hero-badge-text" className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-limebright">{t(content.hero.badge)}</span>
          </motion.div>

          <p data-testid="hero-business-areas" className="mb-5 block w-fit max-w-full break-words border-l-2 border-brand-limebright bg-black/70 px-3 py-2 font-display text-base font-bold uppercase leading-relaxed text-white sm:text-lg lg:text-xl">{t(content.hero.eyebrow)}</p>

          <h1 className="font-display text-4xl font-bold leading-[1.02] tracking-tight text-white min-[380px]:text-5xl sm:text-6xl lg:text-8xl break-words" data-testid="hero-title">
            {t(lines.map((line, i) =>
            <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}>
                
                  {t(i === 1 ? <span data-testid="hero-highlight-text" className="box-decoration-clone rounded-sm bg-black/80 px-2 text-brand-limebright">{t(line)}</span> : line)}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg"
            data-testid="hero-subtitle">
            
            {t(content.hero.subtitle)}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4">
            
            <Link
              to="/preventivo"
              data-testid="hero-contact-button"
              className="group inline-flex items-center gap-2 rounded-full bg-brand-lime px-7 py-3.5 font-display text-sm font-bold text-brand-navydeep transition-all duration-300 hover:bg-white">{t("Richiedi un preventivo")}


              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/lavori"
              data-testid="hero-works-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 font-display text-sm font-bold text-white transition-colors duration-300 hover:border-brand-lime hover:text-brand-lime">{t("Scopri i nostri lavori ")}

              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50">
          
          <ChevronDown className="h-6 w-6 animate-bounce" />
        </motion.div>
      </section>

      <Marquee items={content.marquee} />

      {/* STATS */}
      <section className="bg-white" data-testid="stats-section">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-slate-100 px-4 py-14 sm:px-6 lg:grid-cols-4 lg:px-8">
          {t(content.stats.map((s, i) =>
          <Reveal key={i} delay={i * 0.08} className="group bg-white p-6">
              <p className="font-display text-5xl font-bold tracking-tight text-brand-navy transition-colors duration-300 group-hover:text-brand-limeink sm:text-6xl">
                <Counter value={s.value} />
              </p>
              <p className="mt-2 text-sm font-medium text-slate-500">{t(s.label)}</p>
              <span className="mt-4 block h-0.5 w-8 bg-brand-lime transition-all duration-500 group-hover:w-16" />
            </Reveal>
          ))}
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="relative bg-[#F8FAFC] py-20 lg:py-28" data-testid="about-teaser">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-2xl border-2 border-brand-lime" />
              <img
                data-testid="home-about-team-image"
                src={content.about.image}
                alt={t("Professionisti riuniti per la revisione di un progetto edilizio")}
                className="img-spotlight relative aspect-[4/3] w-full rounded-2xl object-cover" />
              
              <div className="absolute -bottom-6 -right-4 rounded-xl bg-brand-navy px-6 py-4 text-white shadow-xl sm:-right-6">
                <p className="font-display text-3xl font-bold text-brand-lime">50+</p>
                <p className="text-xs text-white/70">{t("aziende partner fidelizzate")}</p>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal><Eyebrow>{t("Chi siamo")}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-navydeep sm:text-4xl">
                {t(content.about.title)}
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-5 leading-relaxed text-slate-600">
                {t(content.about.text.split("\n\n")[0])}
              </p>
              <p data-testid="home-capital" className="mt-5 inline-block rounded-lg border border-brand-amber/30 bg-brand-amberlight px-4 py-2 text-sm font-semibold text-brand-amberdark">{t("Capitale sociale ")}{t(content.contact.capital)}</p>
            </Reveal>
            <Reveal delay={0.26}>
              <Link
                to="/chi-siamo"
                data-testid="about-teaser-link"
                className="mt-7 inline-flex items-center gap-2 font-display text-sm font-bold text-brand-navy transition-colors hover:text-brand-limeink">{t("Scopri l'azienda ")}

                <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
        <DecorativeStripe testId="home-about-decorative-stripe" />
      </section>

      {/* GALLERIA */}
      <HomeWorkGallery />

      {/* SERVIZI BENTO */}
      <section className="relative bg-white py-20 lg:py-28" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal><Eyebrow>{t("Cosa facciamo")}</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-brand-navydeep sm:text-4xl">{t("Competenze diverse, un’unica regia")}

            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-6">
            {t(content.services.map((s, i) =>
            <Reveal key={i} delay={i * 0.05} className={BENTO_SPANS[i % BENTO_SPANS.length]}>
                <div
                data-testid={`service-card-${i}`}
                className="group flex h-full flex-col justify-between rounded-2xl border border-slate-200/80 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-brand-lime hover:shadow-lg">
                
                  <div data-testid={`service-icon-${i}`} className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-limelight text-brand-navy transition-colors duration-300 group-hover:bg-brand-navy group-hover:text-brand-lime">
                    <DynIcon name={s.icon} className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 [overflow-wrap:anywhere]">
                    <h3 data-testid={`service-title-${i}`} className="font-display text-lg font-bold text-brand-navydeep">{t(s.title)}</h3>
                    {t(s.icon === "DraftingCompass" && <span aria-hidden="true" data-testid="design-service-accent-line" className="my-4 block h-px w-12 bg-brand-amber" />)}
                    <p data-testid={`service-description-${i}`} className="mt-2 text-sm leading-relaxed text-slate-500">{t(s.desc)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <DecorativeStripe testId="home-services-decorative-stripe" />
      </section>

      <Macroareas />

      {/* PARCO MEZZI */}
      <section className="relative bg-brand-navydeep py-20 text-white lg:py-28" data-testid="fleet-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <Reveal><Eyebrow light>{t("Parco mezzi")}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("Mezzi propri, ")}
                <span className="text-brand-lime">{t("zero attese")}</span>
              </h2>
            </Reveal>
            <div className="mt-10 space-y-1">
              {t(content.fleet.map((f, i) =>
              <Reveal key={i} delay={i * 0.05}>
                  <div data-testid={`fleet-item-${i}`} className="group flex items-start gap-4 border-l-2 border-white/15 py-4 pl-5 transition-colors duration-300 hover:border-brand-lime">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 text-brand-lime transition-colors duration-300 group-hover:bg-brand-lime group-hover:text-brand-navydeep">
                      <DynIcon name={f.icon} className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 data-testid={`fleet-title-${i}`} className="font-display font-bold text-white transition-colors group-hover:text-brand-lime">{t(f.title)}</h3>
                      <p data-testid={`fleet-description-${i}`} className="text-sm text-white/60">{t(f.desc)}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {t(content.fleetImages.map((src, i) =>
            <Reveal key={i} delay={i * 0.1} className={i === 0 ? "col-span-2" : ""}>
                <img
                src={src}
                alt={t(`Mezzo aziendale GFC ${i + 1}`)}
                className={`img-spotlight w-full rounded-2xl object-cover ${i === 0 ? "aspect-[16/9]" : "aspect-square"}`} />
              
              </Reveal>
            ))}
          </div>
        </div>
        <DecorativeStripe testId="home-fleet-decorative-stripe" />
      </section>

      <SportCommunity />

      {/* CERTIFICAZIONI STRIP */}
      <section className="gfc-home-certifications py-20" data-testid="certs-strip">
        <CertificationBackdrop />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal><Eyebrow>{t("Qualità certificata")}</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 data-testid="home-certifications-title" className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-navydeep sm:text-4xl">{t("Le nostre certificazioni")}

            </h2>
          </Reveal>
          <div className="mt-10 flex flex-wrap gap-3">
            {t(content.certifications.map((c, i) =>
            <Reveal key={i} delay={i * 0.05}>
                <span
                data-testid={`cert-badge-${i}`}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 font-mono text-sm font-semibold text-brand-navy shadow-sm transition-all duration-300 hover:border-brand-lime hover:bg-brand-limelight">
                
                  {t(c.org && c.org.includes("Perry Johnson") ?
                <img src="/partners/perry-johnson.svg" alt={t("Logo Perry Johnson Registrars")} className="h-4 w-4 object-contain" /> :

                <ShieldCheck className="h-4 w-4 text-brand-limeink" />)
                } {t(c.code)}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link to="/certificazioni" data-testid="certs-strip-link" className="mt-8 inline-flex items-center gap-2 font-display text-sm font-bold text-brand-navy hover:text-brand-limeink">{t("Tutti i dettagli ")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <DesignPhilosophySection />

      <BrochureDownload placement="home" />

      <CareerCallout />

      {/* CTA */}
      <section className="bg-brand-lime py-20" data-testid="cta-banner">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-8">
          <Reveal>
            <h2 className="font-display text-3xl font-bold tracking-tight text-brand-navydeep sm:text-4xl">{t("Hai un progetto da realizzare?")}

            </h2>
            <p className="mt-3 max-w-xl text-brand-navydeep/70">{t("Raccontaci la tua esigenza: l'ufficio tecnico ti risponde con una proposta concreta.")}

            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/preventivo"
              data-testid="cta-banner-button"
              className="inline-flex items-center gap-2 rounded-full bg-brand-navydeep px-8 py-4 font-display text-sm font-bold text-white transition-transform duration-300 hover:scale-105">{t("Formula un preventivo ")}

              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>);

}
